import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, NavLink, Link } from 'react-router-dom';
import {
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  AppBar,
  Toolbar,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import logo from '../img/motionair_icon_blue.svg';
import NotificationBell from '../Components/NotificationBell.jsx';
import LogbookInboxBell from '../Components/LogbookInboxBell.jsx';
import FallingFlakes from '../Components/ChristmasComponents/FallingFlakes.jsx';
import { useAuth } from '../Hooks/useAuth';
import isElectron from '../Utils/isElectron';

/**
 * App header layout with navigation, logbook inbox, offline indicator,
 * and conditional Electron UI. Status icons sit at the right of the bar.
 */
const Layout = ({ hideHeaderPaths = [] }) => {
  const { handleLogout } = useAuth();
  // FIX 1: We will ONLY use pathname from useLocation for all path logic.
  const { pathname } = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);

  // This check now correctly uses the pathname from the hook
  const showHeader = !hideHeaderPaths.includes(pathname);

  // This check is for conditionally rendering the Electron window controls
  const isElectronEnv = isElectron();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  const navLinks = [
    { path: "/calendar", text: "Calendar" },
    { path: "/agenda", text: "Agenda" },
    { path: "/todo", text: "Todo" },
    { path: "/resources", text: "Resources" },
    // Only show settings in web app, not in Electron
    ...(isElectronEnv ? [] : [{ path: "/settings", text: "Settings" }]),
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {showHeader && (
        <AppBar
          position="fixed"
          sx={{
            backgroundColor: "#2e2e2e",
            boxShadow: "none",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            overflow: 'visible', // Allow flakes to be visible
            '&.MuiAppBar-root': {
              backgroundColor: "#2e2e2e",
            },
          }}
        >
          {/* Falling Snow Animation */}
            <FallingFlakes height={64} />
          <Toolbar sx={{ justifyContent: "space-between", paddingX: "16px", position: "relative", zIndex: 2 }}>
            {/* FIX 2: conditionally render a spacer for the electron window controls */}
            {isElectronEnv ? (
              <div style={{width: "20px"}}></div>
            ) : (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={toggleMobileMenu}
                sx={{ display: { md: "none" } }}
              >
                <MenuIcon sx={{ color: "#3498B7" }} />
              </IconButton>
            )}

            {!isElectronEnv && (
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
                <Link to="/calendar">
                  <img
                    className="logo-image"
                    src={logo}
                    alt="Logo"
                    style={{ height: "40px" }}
                  />
                </Link>
                {/* Optional left-side indicator removed per new requirements */}
              </Box>
            )}
            {/* Desktop Navigation */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                marginRight: "auto",
                marginLeft: "20px",
              }}
            >
              {navLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) =>
                    isActive ? "active regular" : "regular"
                  }
                  style={{ marginRight: "20px" }}
                >
                  {link.text}
                </NavLink>
              ))}
              {!isElectronEnv && (
                <button type="button" className="nav-log-out regular" onClick={handleLogout}>
                  Log out
                </button>
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: 'auto', justifyContent: 'flex-end' }} style={{ WebkitAppRegion: 'no-drag' }}>
              {!isOnline && (
                <div className="header-offline-pill">
                  Offline
                </div>
              )}
              <LogbookInboxBell />
              <NotificationBell inline showWhenOnline={false} />
              {/* Top-right offline badge is globally rendered in App.js; removed local wrapper */}
            </Box>

            {/* Removed mobile spacer; right content is right-aligned */}
          </Toolbar>
        </AppBar>
      )}

      {/* This Toolbar acts as a spacer to push content below the fixed AppBar */}
      {showHeader && <Toolbar />}

      {/* Mobile Drawer Navigation */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        sx={{
          display: { md: "none" },
          '& .MuiDrawer-paper': {
            top: { xs: 56, sm: 64 },
            height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' }
          }
        }}
      >
        <Box
          sx={{
            width: 250,
            backgroundColor: "#2e2e2e",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
          role="presentation"
          onClick={toggleMobileMenu}
          onKeyDown={toggleMobileMenu}
        >
          {/* Mobile-only logo now shown inside the drawer */}
          {!isElectronEnv && (
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', justifyContent: 'center', py: 2 }}>
              <Link to="/calendar">
                <img
                  className="logo-image"
                  src={logo}
                  alt="Logo"
                  style={{ height: "40px" }}
                />
              </Link>
            </Box>
          )}
          <List sx={{ flex: 1 }}>
            {navLinks.map((link) => (
              <ListItem key={link.text} disablePadding>
                <ListItemButton component={Link} to={link.path}>
                  <ListItemText
                    primary={link.text}
                    sx={{
                      color: pathname === link.path ? "#3598b7" : "#D4D4D4",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          {!isElectronEnv && (
            <List sx={{ borderTop: "1px solid rgba(255,255,255,0.12)" }}>
              <ListItem disablePadding>
                <ListItemButton onClick={handleLogout}>
                  <ListItemText primary="Log out" sx={{ color: "#D4D4D4" }} />
                </ListItemButton>
              </ListItem>
            </List>
          )}
        </Box>
      </Drawer>

      <main className="app-content">
        <Outlet />
      </main>
    </Box>
  );
};

export default Layout;
